import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { knownAddressService } from '../services/api';
import '../styles/KnownAddresses.css';

const ADDRESS_TYPES = ['contract', 'exchange', 'mixer', 'sanctioned', 'bridge', 'defi', 'other'];

const TYPE_META = {
  contract:   { color: '#a855f7', label: 'Smart Contract', icon: '📜' },
  exchange:   { color: '#f59e0b', label: 'Exchange',        icon: '🏦' },
  mixer:      { color: '#ef4444', label: 'Mixer',           icon: '🔀' },
  sanctioned: { color: '#dc2626', label: 'Sanctioned',      icon: '🚫' },
  bridge:     { color: '#06b6d4', label: 'Bridge',          icon: '🌉' },
  defi:       { color: '#10b981', label: 'DeFi Protocol',   icon: '⚡' },
  other:      { color: '#6b7280', label: 'Other',           icon: '🏷️' },
};

const BLOCKCHAINS = ['tron', 'ethereum', 'polygon', 'arbitrum', 'bitcoin', 'solana'];
const BLOCKCHAIN_LABELS = {
  tron: 'Tron', ethereum: 'Ethereum', polygon: 'Polygon',
  arbitrum: 'Arbitrum', bitcoin: 'Bitcoin', solana: 'Solana',
};

const BLANK_FORM = {
  address: '', blockchain: 'tron', address_type: 'contract',
  name: '', symbol: '', notes: '',
};

// Pre-seeded well-known addresses to make getting started easy
const PRESETS = [
  { address: 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t', blockchain: 'tron',     address_type: 'contract', name: 'Tether USD',        symbol: 'USDT', notes: 'Official USDT TRC20 contract' },
  { address: 'TEkxiTehnzSmSe2XqrBj4w32RUN966rdz8', blockchain: 'tron',     address_type: 'exchange', name: 'Binance Hot Wallet', symbol: '',     notes: 'Binance TRON hot wallet' },
  { address: 'TKzxdSv2FZKQrEqkKVgp5DcwEXBEKMg2Ax', blockchain: 'tron',     address_type: 'exchange', name: 'OKX Hot Wallet',     symbol: '',     notes: 'OKX TRON hot wallet' },
  { address: '0xdAC17F958D2ee523a2206206994597C13D831ec7', blockchain: 'ethereum', address_type: 'contract', name: 'Tether USD',   symbol: 'USDT', notes: 'Official USDT ERC20 contract' },
  { address: '0x2260FAC5E5542a773Aa44fBCfeDf7C193bc2C599', blockchain: 'ethereum', address_type: 'contract', name: 'Wrapped Bitcoin', symbol: 'WBTC', notes: 'WBTC ERC20 contract' },
  { address: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', blockchain: 'ethereum', address_type: 'contract', name: 'USD Coin',        symbol: 'USDC', notes: 'Official USDC ERC20 contract' },
  { address: '0x28C6c06298d514Db089934071355E5743bf21d60', blockchain: 'ethereum', address_type: 'exchange', name: 'Binance Hot Wallet', symbol: '', notes: 'Binance ETH hot wallet' },
];

export default function KnownAddresses() {
  const navigate  = useNavigate();
  const { token } = useAuthStore();

  const [entries, setEntries]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [form, setForm]                 = useState(BLANK_FORM);
  const [submitting, setSubmitting]     = useState(false);
  const [error, setError]               = useState(null);
  const [success, setSuccess]           = useState(null);
  const [filterType, setFilterType]     = useState('');
  const [filterChain, setFilterChain]   = useState('');
  const [search, setSearch]             = useState('');
  const [seeding, setSeeding]           = useState(false);

  const loadEntries = useCallback(async () => {
    try {
      setLoading(true);
      const res = await knownAddressService.list();
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setError('Failed to load registry');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    loadEntries();
  }, [token, navigate, loadEntries]);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(null), 4000); }
    else         { setSuccess(msg); setTimeout(() => setSuccess(null), 3000); }
  };

  const handleSubmit = async () => {
    if (!form.address.trim() || !form.name.trim()) {
      flash('Address and Name are required', true); return;
    }
    try {
      setSubmitting(true);
      if (editingId) {
        await knownAddressService.update(editingId, {
          address_type: form.address_type, name: form.name,
          symbol: form.symbol, notes: form.notes,
        });
        flash('Entry updated');
      } else {
        await knownAddressService.create(form);
        flash('Address added to registry');
      }
      setForm(BLANK_FORM); setShowForm(false); setEditingId(null);
      loadEntries();
    } catch (e) {
      flash(e.response?.data?.detail || 'Save failed', true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry) => {
    setForm({
      address: entry.address, blockchain: entry.blockchain,
      address_type: entry.address_type, name: entry.name,
      symbol: entry.symbol || '', notes: entry.notes || '',
    });
    setEditingId(entry.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the registry?`)) return;
    try {
      await knownAddressService.delete(id);
      flash(`"${name}" removed`);
      loadEntries();
    } catch (e) {
      flash('Delete failed', true);
    }
  };

  const handleCancel = () => {
    setForm(BLANK_FORM); setShowForm(false); setEditingId(null); setError(null);
  };

  const handleSeedPresets = async () => {
    setSeeding(true);
    let added = 0; let skipped = 0;
    for (const preset of PRESETS) {
      try {
        await knownAddressService.create(preset);
        added++;
      } catch (e) {
        if (e.response?.status === 409) skipped++;
      }
    }
    flash(`Seeded ${added} preset address${added !== 1 ? 'es' : ''} (${skipped} already existed)`);
    setSeeding(false);
    loadEntries();
  };

  // ── Filtered view ──
  const visible = entries.filter(e => {
    if (filterType  && e.address_type !== filterType)  return false;
    if (filterChain && e.blockchain   !== filterChain) return false;
    if (search) {
      const q = search.toLowerCase();
      return e.address.toLowerCase().includes(q) ||
             e.name.toLowerCase().includes(q) ||
             (e.symbol || '').toLowerCase().includes(q);
    }
    return true;
  });

  const counts = ADDRESS_TYPES.reduce((acc, t) => {
    acc[t] = entries.filter(e => e.address_type === t).length;
    return acc;
  }, {});

  return (
    <div className="ka-page">

      {/* Header */}
      <header className="ka-header">
        <button className="ka-back" onClick={() => navigate('/dashboard')}>← Back</button>
        <div className="ka-header-title">
          <h1>🗂️ Known Address Registry</h1>
          <p>Global database of smart contracts, exchange wallets, and other known addresses</p>
        </div>
        <div className="ka-header-actions">
          <button className="ka-btn ka-btn-outline" onClick={handleSeedPresets} disabled={seeding}>
            {seeding ? '⏳ Seeding…' : '🌱 Seed Presets'}
          </button>
          <button className="ka-btn ka-btn-primary" onClick={() => { handleCancel(); setShowForm(true); }}>
            + Add Address
          </button>
        </div>
      </header>

      {/* Flash messages */}
      {error   && <div className="ka-flash ka-flash-error">⚠ {error}</div>}
      {success && <div className="ka-flash ka-flash-success">✓ {success}</div>}

      {/* Type summary pills */}
      <div className="ka-type-summary">
        {ADDRESS_TYPES.map(t => (
          <button key={t}
            className={`ka-type-pill ${filterType === t ? 'active' : ''}`}
            style={{ '--pill-color': TYPE_META[t].color }}
            onClick={() => setFilterType(filterType === t ? '' : t)}>
            {TYPE_META[t].icon} {TYPE_META[t].label}
            <span className="pill-count">{counts[t] || 0}</span>
          </button>
        ))}
      </div>

      {/* Add / Edit form */}
      {showForm && (
        <div className="ka-form-card">
          <h3>{editingId ? '✏️ Edit Entry' : '➕ Add Known Address'}</h3>

          <div className="ka-form-grid">
            <div className="ka-field ka-field-full">
              <label>Address *</label>
              <input type="text"
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="Full address (e.g. TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t)"
                disabled={!!editingId}
                className="ka-input ka-mono"
              />
            </div>

            <div className="ka-field">
              <label>Blockchain *</label>
              <select value={form.blockchain}
                onChange={e => setForm({ ...form, blockchain: e.target.value })}
                disabled={!!editingId}
                className="ka-select">
                {BLOCKCHAINS.map(b => <option key={b} value={b}>{BLOCKCHAIN_LABELS[b] || b}</option>)}
              </select>
            </div>

            <div className="ka-field">
              <label>Type *</label>
              <select value={form.address_type}
                onChange={e => setForm({ ...form, address_type: e.target.value })}
                className="ka-select">
                {ADDRESS_TYPES.map(t => (
                  <option key={t} value={t}>{TYPE_META[t].icon} {TYPE_META[t].label}</option>
                ))}
              </select>
            </div>

            <div className="ka-field">
              <label>Name *</label>
              <input type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Tether USD, Binance Hot Wallet"
                className="ka-input"
              />
            </div>

            <div className="ka-field">
              <label>Symbol <span className="ka-optional">(optional)</span></label>
              <input type="text"
                value={form.symbol}
                onChange={e => setForm({ ...form, symbol: e.target.value })}
                placeholder="e.g. USDT, BTC"
                className="ka-input"
              />
            </div>

            <div className="ka-field ka-field-full">
              <label>Notes <span className="ka-optional">(optional)</span></label>
              <textarea value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Additional context..."
                className="ka-textarea"
                rows={2}
              />
            </div>
          </div>

          {/* Type colour preview */}
          <div className="ka-type-preview" style={{ borderColor: TYPE_META[form.address_type].color }}>
            <span className="ka-type-dot" style={{ background: TYPE_META[form.address_type].color }} />
            This address will appear as{' '}
            <strong style={{ color: TYPE_META[form.address_type].color }}>
              {TYPE_META[form.address_type].icon} {TYPE_META[form.address_type].label}
            </strong>
            {' '}in the graph
          </div>

          <div className="ka-form-actions">
            <button className="ka-btn ka-btn-ghost" onClick={handleCancel}>Cancel</button>
            <button className="ka-btn ka-btn-primary" onClick={handleSubmit} disabled={submitting}>
              {submitting ? '⏳ Saving…' : editingId ? '💾 Save Changes' : '✓ Add to Registry'}
            </button>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="ka-filters">
        <input type="text" placeholder="🔍 Search by name, symbol or address…"
          value={search} onChange={e => setSearch(e.target.value)}
          className="ka-search" />
        <select value={filterChain} onChange={e => setFilterChain(e.target.value)} className="ka-select-sm">
          <option value="">All Blockchains</option>
          {BLOCKCHAINS.map(b => <option key={b} value={b}>{BLOCKCHAIN_LABELS[b] || b}</option>)}
        </select>
        {(filterType || filterChain || search) && (
          <button className="ka-btn-clear" onClick={() => { setFilterType(''); setFilterChain(''); setSearch(''); }}>
            ✕ Clear filters
          </button>
        )}
        <span className="ka-count">{visible.length} of {entries.length} entries</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="ka-loading">Loading registry…</div>
      ) : visible.length === 0 ? (
        <div className="ka-empty">
          {entries.length === 0
            ? <><p>No known addresses yet.</p><p>Click <strong>+ Add Address</strong> or <strong>🌱 Seed Presets</strong> to get started.</p></>
            : <p>No entries match your filters.</p>
          }
        </div>
      ) : (
        <div className="ka-table-wrap">
          <table className="ka-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name / Symbol</th>
                <th>Address</th>
                <th>Chain</th>
                <th>Notes</th>
                <th>Added by</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(entry => {
                const meta = TYPE_META[entry.address_type] || TYPE_META.other;
                return (
                  <tr key={entry.id}>
                    <td>
                      <span className="ka-badge" style={{ background: meta.color + '22', color: meta.color, borderColor: meta.color + '55' }}>
                        {meta.icon} {meta.label}
                      </span>
                    </td>
                    <td>
                      <div className="ka-name">{entry.name}</div>
                      {entry.symbol && <div className="ka-symbol">{entry.symbol}</div>}
                    </td>
                    <td>
                      <code className="ka-addr">{entry.address}</code>
                    </td>
                    <td>
                      <span className="ka-chain">{entry.blockchain}</span>
                    </td>
                    <td className="ka-notes-cell">{entry.notes || '—'}</td>
                    <td className="ka-added-by">{entry.added_by || '—'}</td>
                    <td>
                      <div className="ka-row-actions">
                        <button className="ka-btn-edit" onClick={() => handleEdit(entry)}>✏️</button>
                        <button className="ka-btn-del"  onClick={() => handleDelete(entry.id, entry.name)}>🗑️</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
